import React, { FunctionComponent, useRef } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import pLimit from 'p-limit';
import formsPreviewImage from '../../../assets/guides/forms-preview.png';
import { DealerLaunchStepProps } from '../types';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import { Checkbox } from '../../Input/Checkbox/Checkbox';
import { PreviewGuide } from '../../QuickGuide/QuickGuide';
import FormTemplateService from '../../../services/FormTemplateService';
import { ErrorMessage } from '../../ErrorMessage/ErrorMessage';
import { Spinner } from '../../Spinner/Spinner';
import { toHex } from '../../../utils/format';
import { Button } from '../../Input/Button/Button';
import styles from '../styles.module.scss';

interface StandardFormEntry {
  name: string;
  url: string;
}

const STANDARD_FORMS: StandardFormEntry[] = [
  {
    name: 'Volkswagen Credit Business Credit Application',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-BusinessCreditApp.pdf',
  },
  {
    name: 'Buyers Guide',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-BuyersGuide.pdf',
  },
  {
    name: 'Reynolds & Reynolds Credit Application',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-CreditApp.pdf',
  },
  {
    name: 'Dealertrack Credit Application',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-DealertrackCreditApp.pdf',
  },
  {
    name: 'Toyota Vehicle Delivery Checklist',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-NewVehicleDeliveryChecklist.pdf',
  },
  {
    name: 'References Sheet',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-ReferencesSheet.pdf',
  },
  {
    name: 'RouteOne Credit Application',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-Route1CreditApp.pdf',
  },
  {
    name: 'Southeast Toyota (SET) Credit Application',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-SETCreditApp.pdf',
  },
  {
    name: 'Test Drive Form',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-TestDriveForm.pdf',
  },
  {
    name: 'Trade Appraisal',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-TradeAppraisal.pdf',
  },
  {
    name: 'Vin Verification Form',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-VinVerificationForm.pdf',
  },
  {
    name: 'We Owe',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-WeOwe.pdf',
  },
  {
    name: 'We Owe You Owe',
    url:
      'https://prodigy-cdn.s3-us-west-1.amazonaws.com/launchpad/standard-forms/PZ-WeOweYouOwe.pdf',
  },
];

interface FormData {
  // map of the hex of the standard form's url to whether it's enabled
  formsEnabled?: { [index: string]: boolean };
}

export const DealerLaunchInstoreForms: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const form = useLaunchpadForm<FormData>();
  const formRef = useRef(form);
  formRef.current = form;
  // get the current form list for the dealer
  const formTemplates = useAsync(FormTemplateService.getAll, [dealer.id], {
    // reset the form when the data gets loaded
    onSuccess: ({ data }) =>
      requestAnimationFrame(() => {
        const formsEnabled: { [index: string]: boolean } = {};
        // mark whether a standard form is enabled or not based on if there's a form template
        STANDARD_FORMS.forEach((formEntry) => {
          formsEnabled[toHex(formEntry.url)] = !!data.find(
            (f) => f.url === formEntry.url,
          );
        });
        formRef.current.reset({ formsEnabled });
      }),
  });

  // creates multiple form templates from a list of StandardFormEntry
  const createForms = useAsyncCallback((forms: StandardFormEntry[]) => {
    const limit = pLimit(5);
    // limit the number of form creation requests
    return Promise.all(
      forms.map((formEntry) =>
        limit(() =>
          FormTemplateService.create({
            name: formEntry.name,
            url: formEntry.url,
            dealer_id: dealer.id,
          }),
        ),
      ),
    );
  });
  // deletes multiple form templates from a list of form template ids
  const deleteForms = useAsyncCallback((formIds: string[]) => {
    const limit = pLimit(5);
    // limit the number of form deletion requests
    return Promise.all(
      formIds.map((formId) => limit(() => FormTemplateService.delete(formId))),
    );
  });

  const onSubmit = async (data: FormData) => {
    const formsToAdd: StandardFormEntry[] = [];
    const formsToDelete: string[] = [];

    // go through all the forms and figure out if an action needs to be taken
    STANDARD_FORMS.forEach((formEntry) => {
      const savedFormTemplate = formTemplates.result?.data.find(
        (f) => f.url === formEntry.url,
      );
      if (data.formsEnabled?.[toHex(formEntry.url)]) {
        if (!savedFormTemplate) {
          // if the form is now enabled but it's not saved, queue it to be saved
          formsToAdd.push(formEntry);
        }
      } else if (savedFormTemplate) {
        // the the form is not enabled but it's saved, queue it to be deleted
        formsToDelete.push(savedFormTemplate.id);
      }
    });

    // execute the queued operations
    if (formsToAdd.length) await createForms.execute(formsToAdd);
    if (formsToDelete.length) await deleteForms.execute(formsToDelete);

    await nextStep({ launchpad: { status: { finishedFormsConfig: true } } });
  };

  return (
    <DealerLaunchFormCardLayout
      className={styles.formsStep}
      form={form}
      title="Let's Have A Look At What Forms You Want"
      subTitle="Below are standard forms available for all dealerships. These forms will be available on the in-store product and should include any forms in your deal packs."
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      {formTemplates.loading && <Spinner variant="fullscreen" />}
      <ErrorMessage
        error={formTemplates.error || createForms.error || deleteForms.error}
      />
      <div className="flex-row">
        <h1>Standard Forms</h1>
        <div className="spacer" />
        <PreviewGuide
          action="Show Where This is Used"
          guideTitle="Where we use forms"
        >
          <div>
            We offer the ability to view, edit, and print forms directly from
            our in-store solution as you&apos;re making a deal.
            <img src={formsPreviewImage} alt="Forms Preview" />
            Please note that this is not a live preview.
          </div>
        </PreviewGuide>
      </div>
      {STANDARD_FORMS.map((formEntry) => (
        <div className="flex-row">
          <Checkbox
            key={formEntry.url}
            label={formEntry.name}
            name={`formsEnabled[${toHex(formEntry.url)}]` as never}
            form={form}
          />
          <Button
            className={styles.previewButton}
            label="Preview"
            variant="minimal"
            onClick={() => window.open(formEntry.url, '_blank')}
          />
        </div>
      ))}
    </DealerLaunchFormCardLayout>
  );
};
